import React, { useMemo, useState } from 'react';
import styles from './styles.module.scss';
import { IHolder } from '@/interfaces/holder';
import clsx from 'clsx';
import InfoTooltip from '@/modules/AlphaPWA/Profiles/TradeKey/components/infoTooltip';
import { Box, Flex, Text } from '@chakra-ui/react';
import Avatar from '../../Profiles/TradeKey/components/avatar';
import { getUrlAvatarTwitter } from '@/utils/helpers';
import { formatCurrency, formatName } from '@/utils/string';
import { MAX_DECIMAL, MIN_DECIMAL } from '../../Profiles/TradeKey/constants';
import { useProfiles } from '../../Profiles/provider';
import { IGetPlayerPoolProfile } from '@/interfaces/api/player-share';

interface props {
  data: IHolder[];
  type?: 'CIRCLE' | 'SQUARE';
  playerProfile?: IGetPlayerPoolProfile | null;
}

const CircleHolders = ({ data, type = 'CIRCLE', playerProfile }: props) => {
  const { playerPoolProfile } = useProfiles();
  const isCircle = type === 'CIRCLE';

  const playerPoolProfileFn = useMemo(() => {
    return playerProfile || playerPoolProfile;
  }, [playerProfile, playerPoolProfile]);

  const les2 = isCircle ? 12 : 5 * 3 + 1;
  const les3 = les2 + 24;
  const d2 = data.slice(0, les2);
  const d3 = data?.slice(les2, les3);

  const renderIcon = ({
    holder,
    round,
  }: {
    holder: IHolder;
    round: number;
  }) => {
    const [isOpen, setIsOpen] = useState<boolean>(false);

    return (
      <li className={isOpen ? styles.isOpen : ''}>
        <InfoTooltip
          setIsOpen={setIsOpen}
          label={
            <>
              <Flex alignItems={'center'} gap={2}>
                <Avatar
                  url={getUrlAvatarTwitter(
                    (holder?.user?.twitter_avatar ||
                      holder?.user?.avatar) as string
                  )}
                  address={holder?.user?.address || ''}
                  name={holder?.user?.twitter_name || ''}
                  width={32}
                  fontSize={14}
                />
                <Box color={'#2CAFFEFF'} fontWeight={600}>
                  {formatName(
                    holder?.user?.twitter_name || holder?.user?.address || '',
                    30
                  )}
                </Box>
              </Flex>
              <Text fontSize={'14px'} mt={2}>
                {formatCurrency(holder?.balance, MIN_DECIMAL, MAX_DECIMAL)} key
                {Number(holder?.balance) === 1 ? '' : 's'}
              </Text>
            </>
          }
        >
          <Avatar
            url={getUrlAvatarTwitter(
              (holder?.user?.twitter_avatar || holder?.user?.avatar) as string,
              'medium'
            )}
            address={holder?.user?.address || ''}
            name={holder?.user?.twitter_name || ''}
            width={round === 1 ? 130 : round === 2 ? 40 : 40}
            fontSize={24}
          />
        </InfoTooltip>
      </li>
    );
  };

  const renderIconNone = ({
    twUrl,
    twitter_name,
    address,
    round,
  }: {
    twUrl: string;
    twitter_name: string;
    address: string;
    round: number;
  }) => {
    return (
      <li>
        <Avatar
          url={getUrlAvatarTwitter(twUrl as string, 'medium')}
          address={address || ''}
          name={twitter_name || ''}
          width={round === 1 ? 130 : round === 2 ? 40 : 40}
          fontSize={24}
        />
      </li>
    );
  };

  return isCircle ? (
    <div className={styles.circleContainerWrapper}>
      <ul className={clsx(styles.circleContainer, styles.round2)}>
        {d2.map(holder =>
          renderIconNone({
            twUrl: holder?.user?.twitter_avatar || holder?.user?.avatar || '',
            address: holder?.user?.address || '',
            twitter_name: holder?.user?.twitter_name || '',
            round: 2,
          })
        )}
      </ul>
      <ul className={clsx(styles.circleContainer, styles.round1)}>
        {renderIconNone({
          twUrl:
            playerPoolProfileFn?.twitterAvatar ||
            playerPoolProfileFn?.avatar ||
            '',
          address: playerPoolProfileFn?.address || '',
          twitter_name: playerPoolProfileFn?.twitterName || '',
          round: 1,
        })}
      </ul>
    </div>
  ) : (
    <div className={styles.squareContainerWrapper}>
      <ul className={styles.s1}>
        {renderIconNone({
          twUrl:
            playerPoolProfileFn?.twitterAvatar ||
            playerPoolProfileFn?.avatar ||
            '',
          address: playerPoolProfileFn?.address || '',
          twitter_name: playerPoolProfileFn?.twitterName || '',
          round: 1,
        })}
      </ul>
      {!!d2.length && (
        <ul
          className={`${styles.s2} ${
            styles['len_' + (d2?.length < 4 ? 'fix' : '')]
          }`}
        >
          <li></li>
          {d2.map(holder =>
            renderIconNone({
              twUrl: holder?.user?.twitter_avatar || holder?.user?.avatar || '',
              address: holder?.user?.address || '',
              twitter_name: holder?.user?.twitter_name || '',
              round: 2,
            })
          )}
        </ul>
      )}

      {!!d3?.length && (
        <ul
          className={`${styles.s3} ${
            styles['len_' + (d3?.length < 7 ? 'fix' : '')]
          }`}
        >
          <li></li>
          {d3.map(holder =>
            renderIconNone({
              twUrl: holder?.user?.twitter_avatar || holder?.user?.avatar || '',
              address: holder?.user?.address || '',
              twitter_name: holder?.user?.twitter_name || '',
              round: 3,
            })
          )}
        </ul>
      )}
    </div>
  );
};

export default CircleHolders;
